/**
 * `ChatService` —— 信封收发 + Messenger 可靠送达 + 已读回执 + intro 交换（ARCH §3.8）。
 *
 * 迁移自 `prototype/web/core/messenger.js`（70% 复用，逻辑重构）：
 *  1. pending 队列改走注入的 `IStorage`（原型直连 `core/storage.js`，无法单测）
 *  2. **先验签后解密**，验签失败丢弃并记 `E_SIGNATURE_INVALID`（原型无验签）
 *  3. **邮箱消息同样回 ACK**（原型缺陷：离线消息发起方永远停在「◷ 发送中」）
 *  4. 收信按 `msgId` 幂等，重复投递不会重复入库
 *
 * **只抛 `MeshLinkError`**；UI 只消费 `errorCatalog` 文案。
 */

import { buildEnvelope, openEnvelope, verifyEnvelope } from '@/crypto/envelope';
import { EcdhAesGcmCipher, type ISessionCipher } from '@/crypto/session';
import { base64ToBytes, bytesToBase64 } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import type { IStorage } from '@/storage/IStorage';
import type { ITransport } from '@/net/ITransport';
import { useChatStore } from '@/store/chat';
import { useUiStore } from '@/store/ui';
import type { IdentityService } from './IdentityService';
import type { ChatEnvelope, Contact, Message, MsgStatus, PendingItem } from '@shared/types';

const logger = createLogger('service:chat');

/** ACK 超时：8s 内没收到回执就重试（与原型一致）。 */
const ACK_TIMEOUT_MS = 8_000;
/** 最大重试次数。 */
const MAX_RETRIES = 3;
/** 重试间隔。 */
const RETRY_DELAY_MS = 2_000;

/**
 * intro 握手补发的退避序列（ms）：5s → 10s → 20s → 40s。
 *
 * 累计已排时长受 {@link INTRO_RETRY_TOTAL_CAP_MS} 约束：一旦「已排时长 + 下一步间隔」超过上限即停止。
 * 实测序列为 5s → 15s → 35s（下一步 40s 会使累计达 75s > 60s，故跳过），累计 35s，落在 60s 上限内。
 */
const INTRO_RETRY_BACKOFF_MS = [5_000, 10_000, 20_000, 40_000] as const;
/** intro 补发累计时长上限（ms）：超限即停，保留 pending 交 UI 手动重试。 */
const INTRO_RETRY_TOTAL_CAP_MS = 60_000;

/** UTF-8 编解码。 */
const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** 会话加密器工厂（由容器注入；默认 EcdhAesGcmCipher）。 */
export type SessionCipherFactory = (opts: { encSeed: Uint8Array; encPub: Uint8Array }) => ISessionCipher;

export interface ChatServiceOptions {
  storage: IStorage;
  identity: IdentityService;
  transport: ITransport;
  /** 自定义会话加密器（测试注入 / Phase 2 换 Signal）。 */
  cipher?: ISessionCipher;
  /** 会话加密器工厂（容器注入；缺省时回落到 EcdhAesGcmCipher）。 */
  cipherFactory?: SessionCipherFactory;
}

interface PendingAck {
  msgId: string;
  to: string;
  timer: ReturnType<typeof setTimeout>;
  resolve(): void;
  reject(err: unknown): void;
}

export class ChatService {
  private readonly pendingAcks = new Map<string, PendingAck>();
  private readonly sequences = new Map<string, number>();
  private cipher: ISessionCipher | null;
  private cipherFactory: SessionCipherFactory | null = null;
  private started = false;
  /** intro 补发退避环的定时器（null = 未在环内）。 */
  private introRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /** 已排入的退避步数（选择下一次间隔）。 */
  private introRetryStep = 0;
  /** 退避环累计已排时长（ms），用于累计上限判断。 */
  private introRetryElapsedMs = 0;

  constructor(private readonly opts: ChatServiceOptions) {
    this.cipher = opts.cipher ?? null;
    this.cipherFactory = opts.cipherFactory ?? null;
  }

  // ─────────────── 生命周期 ───────────────

  /** 注册收信回调并恢复未送达队列（页面刷新后调用）。 */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.opts.transport.onEnvelope((env, from) => {
      void this.handleIncoming(env, from).catch((err) => {
        logger.error('收信处理失败', err instanceof Error ? err.message : err);
      });
    });
    void this.refreshContacts()
      .then((contacts) => {
        // 页面刷新 / 解锁后可能仍有「未握手」联系人 → 续跑 intro 补发环
        // （否则 reload 场景下无人会重试，联系人永久 pending）。
        if (contacts.some((c) => c.introState === 'pending' && !c.blocked)) this.startIntroRetry();
      })
      .catch((err) => logger.warn('联系人刷新失败', err instanceof Error ? err.message : err));
    void this.flushPendingQueue();
  }

  stop(): void {
    this.started = false;
    this.stopIntroRetry();
    for (const pending of this.pendingAcks.values()) clearTimeout(pending.timer);
    this.pendingAcks.clear();
  }

  // ─────────────── 通讯录 ───────────────

  /** 拉取全部联系人并同步到 store。 */
  async refreshContacts(): Promise<Contact[]> {
    const contacts = await this.opts.storage.contacts.all();
    contacts.sort((a, b) => (b.lastTs ?? b.updatedAt) - (a.lastTs ?? a.updatedAt));
    useChatStore.getState().setContacts(contacts);
    return contacts;
  }

  /**
   * 添加好友：落库（introState='pending'）→ 发 intro → 尝试直连。
   * intro 完成前不允许发消息（UI 层会按 `introState` 禁用输入框）。
   */
  async addContact(peerId: string, name = ''): Promise<Contact> {
    const trimmed = peerId.trim();
    if (!trimmed || trimmed === this.opts.identity.peerId) {
      throw new MeshLinkError('E_PEERID_INVALID');
    }
    const existing = await this.opts.storage.contacts.get(trimmed);
    const contact: Contact = existing ?? {
      peerId: trimmed,
      did: '',
      name: name || `好友_${trimmed.slice(-4)}`,
      encPub: '',
      signPub: '',
      introState: 'pending',
      unread: 0,
      muted: false,
      blocked: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    contact.name = name || contact.name;
    contact.updatedAt = Date.now();
    await this.opts.storage.contacts.put(contact);
    useChatStore.getState().upsertContact(contact);

    await this.sendIntro(trimmed);
    // 启动 intro 补发退避环：`sendRaw` 静默丢包时（中继断连窗口）intro 会永久丢失，
    // 故以「对方是否回环 intro（introState 转 ready）」为判据驱动补发。
    this.startIntroRetry();
    void this.opts.transport.ensureDirect(trimmed).catch(() => false);
    return contact;
  }

  async removeContact(peerId: string): Promise<void> {
    await this.opts.storage.contacts.delete(peerId);
    await this.opts.storage.messages.deleteByPeer(peerId);
    useChatStore.getState().removeContact(peerId);
    // 退避环清理：仅当已无待握手联系人时停（避免残留 timer；有其它 pending 时不误停其重试）。
    const remaining = await this.opts.storage.contacts.all();
    if (!remaining.some((c) => c.introState === 'pending' && !c.blocked)) this.stopIntroRetry();
  }

  async setBlocked(peerId: string, blocked: boolean): Promise<void> {
    const contact = await this.opts.storage.contacts.get(peerId);
    if (!contact) throw new MeshLinkError('E_INVALID_INPUT', '联系人不存在');
    const next: Contact = { ...contact, blocked, updatedAt: Date.now() };
    await this.opts.storage.contacts.put(next);
    useChatStore.getState().upsertContact(next);
  }

  // ─────────────── 发送 ───────────────

  /** 发文本消息（加密 → 信封 → 可靠发送）。 */
  async sendText(peerId: string, text: string): Promise<Message> {
    const body = text.trim();
    if (!body) throw new MeshLinkError('E_INVALID_INPUT', '消息内容不能为空');

    const keys = this.opts.identity.requireUnlocked();
    const contact = await this.opts.storage.contacts.get(peerId);
    if (!contact || !contact.encPub) {
      throw new MeshLinkError('E_PEERID_INVALID', '对方尚未完成身份交换（intro）');
    }
    if (contact.blocked) throw new MeshLinkError('E_INVALID_INPUT', '对方已被屏蔽');

    const cipher = this.requireCipher();
    const sealed = await cipher.encrypt(encoder.encode(body), contact.encPub, keys.signSeed);

    const envelope = buildEnvelope({
      kind: 'msg',
      from: this.opts.identity.did,
      to: peerId,
      signSeed: keys.signSeed,
      encPub: bytesToBase64(keys.encPub),
      sequence: this.nextSequence(peerId),
      ciphertext: sealed.ciphertext,
      iv: sealed.iv,
    });

    const message: Message = {
      id: envelope.id,
      msgId: envelope.id,
      peerId,
      dir: 'out',
      kind: 'text',
      body,
      ciphertext: sealed.ciphertext,
      iv: sealed.iv,
      signature: envelope.signature,
      ts: envelope.timestamp,
      status: 'sending',
    };
    await this.opts.storage.messages.put(message);
    useChatStore.getState().appendMessage(peerId, message);
    await this.touchContact(peerId, { lastText: body, lastTs: envelope.timestamp });

    void this.sendReliable(peerId, envelope).catch((err) => {
      logger.warn('可靠发送失败', err instanceof Error ? err.message : err);
    });
    return message;
  }

  /** 发送 intro（携带 did / name / encPub / signPub）。 */
  async sendIntro(peerId: string, needIntro = true): Promise<void> {
    const keys = this.opts.identity.requireUnlocked();
    const meta = this.opts.identity.current;
    const envelope = buildEnvelope({
      kind: 'intro',
      from: this.opts.identity.did,
      to: peerId,
      signSeed: keys.signSeed,
      encPub: bytesToBase64(keys.encPub),
      sequence: this.nextSequence(peerId),
      did: this.opts.identity.did,
      name: meta?.name ?? '',
      signPub: bytesToBase64(keys.signPub),
      needIntro,
    });
    // intro 不进 ACK 流程（对端会以 intro 回包，天然完成双向确认）
    this.opts.transport.sendRaw(peerId, envelope);
    logger.info('已发送 intro', peerId.slice(0, 12));
  }

  /**
   * 补发所有「尚未完成握手」的 intro，返回补发条数。
   *
   * **判据是业务状态（`introState === 'pending'`），而非发送失败信号** ——
   * `ITransport.sendRaw` 返回 `void`，且在 `HybridTransport.sendRaw` 内**两处静默吞错**
   * （中继未就绪直接 `return`；`sendMessage` 失败只 `catch` 不重试）。调用方在结构上
   * 无法感知「这一帧到底发没发出去」。中继断连会让 intro 被无声丢弃，因此只能以
   * 「对方回环 intro 是否到达（`introState` 是否转 `ready`）」作为成功判据来驱动补发。
   *
   * 过滤规则：`introState === 'pending'` 且未被 `blocked`。
   */
  async resendPendingIntros(): Promise<number> {
    const contacts = await this.opts.storage.contacts.all();
    const pending = contacts.filter((c) => c.introState === 'pending' && !c.blocked);
    for (const c of pending) {
      await this.sendIntro(c.peerId, true);
    }
    if (pending.length > 0) logger.info('补发待握手 intro', pending.length);
    return pending.length;
  }

  /** 启动 intro 补发退避环（5s→10s→20s→40s，累计上限 60s）。幂等：已在环内则忽略。 */
  startIntroRetry(): void {
    if (this.introRetryTimer) return;
    this.introRetryStep = 0;
    this.introRetryElapsedMs = 0;
    this.scheduleIntroRetry();
  }

  /** 停止并清理退避环（removeContact / 锁定 / 卸载时调用，防残留 timer）。 */
  stopIntroRetry(): void {
    if (this.introRetryTimer) {
      clearTimeout(this.introRetryTimer);
      this.introRetryTimer = null;
    }
    this.introRetryStep = 0;
    this.introRetryElapsedMs = 0;
  }

  /** 排下一次补发；触及累计上限即停（保留 pending，由 UI 暴露手动重试入口）。 */
  private scheduleIntroRetry(): void {
    const delay = INTRO_RETRY_BACKOFF_MS[this.introRetryStep];
    if (delay === undefined || this.introRetryElapsedMs + delay > INTRO_RETRY_TOTAL_CAP_MS) {
      this.introRetryTimer = null;
      logger.warn(
        'intro 补发退避结束（累计 ' + this.introRetryElapsedMs + 'ms），仍有未握手联系人（可在 UI 手动重试）',
      );
      return;
    }
    this.introRetryElapsedMs += delay;
    this.introRetryTimer = setTimeout(() => {
      this.introRetryTimer = null;
      void this.introRetryTick();
    }, delay);
    // Node 环境避免定时器持有事件循环（浏览器无 unref，可选链跳过）。
    (this.introRetryTimer as unknown as { unref?: () => void }).unref?.();
  }

  /** 一个退避 tick：还有 pending 才继续排下一次；全 ready 则收环（空转成本归零）。 */
  private async introRetryTick(): Promise<void> {
    try {
      const resent = await this.resendPendingIntros();
      if (resent === 0) {
        this.introRetryStep = 0;
        this.introRetryElapsedMs = 0;
        return;
      }
    } catch (err) {
      // 锁定（requireUnlocked 抛 E_LOCKED）/ IDB 异常等：**不破环**，按退避继续下一次。
      // 调用点是 `void this.introRetryTick()`，若不在此吞错则该 rejection 无人接 →
      // 退避环静默死亡且无日志。step 递增受 60s 累计上限约束，不会无限重排。
      logger.warn('intro 补发失败，继续退避', err instanceof Error ? err.message : err);
    }
    this.introRetryStep += 1;
    this.scheduleIntroRetry();
  }

  /** 已读回执：把本会话未读的入站消息标为 read 并通知对方。 */
  async markRead(peerId: string): Promise<void> {
    const keys = this.opts.identity.requireUnlocked();
    const contact = await this.opts.storage.contacts.get(peerId);
    if (!contact) return;

    const page = await this.opts.storage.messages.page(peerId, undefined, 50);
    const incoming = page.filter((m) => m.dir === 'in' && m.status !== 'read');
    for (const msg of incoming) {
      await this.opts.storage.messages.updateStatus(msg.msgId, 'read');
      useChatStore.getState().updateStatus(peerId, msg.msgId, 'read');

      const envelope = buildEnvelope({
        kind: 'read',
        from: this.opts.identity.did,
        to: peerId,
        signSeed: keys.signSeed,
        encPub: bytesToBase64(keys.encPub),
        sequence: this.nextSequence(peerId),
        msgId: msg.msgId,
      });
      this.opts.transport.sendRaw(peerId, envelope);
    }

    if (contact.unread > 0) {
      const next: Contact = { ...contact, unread: 0, updatedAt: Date.now() };
      await this.opts.storage.contacts.put(next);
      useChatStore.getState().upsertContact(next);
    }
  }

  // ─────────────── 收信 ───────────────

  /**
   * 收信主流程：**先验签后解密**。
   * 无论消息来自直连、中继实时投递还是离线邮箱，都会回 ACK（原型缺陷修复）。
   */
  async handleIncoming(env: ChatEnvelope, fromPeerId: string): Promise<void> {
    const contact = await this.opts.storage.contacts.get(fromPeerId);

    // ① TOFU 分支：对端**未知**，或**已落库但仍缺公钥**（`addContact` 只写了
    //    `encPub:''/signPub:''/introState:'pending'` 的骨架）。
    //    后者若落到 step ② 的「已知对端先验签」，会拿空串当公钥验签 → 必然 false →
    //    对方的合法 intro 被 reportTampered 丢弃 → `upsertFromIntro` 永不可达 →
    //    主动加好友方永久 pending、`contact.encPub` 恒为空（双向死锁，T03 缺陷3）。
    //    因此「尚未拿到对方公钥」一律等同未知对端处理 intro。
    if (!contact || !contact.signPub) {
      if (env.kind !== 'intro' || !env.signPub || !env.encPub || !env.did) {
        logger.warn('未知/待握手对端的非 intro 消息已丢弃', fromPeerId.slice(0, 12));
        return;
      }
      if (!verifyEnvelope(env, env.signPub)) {
        this.reportTampered(env);
        return;
      }
      await this.upsertFromIntro(env, fromPeerId);
      if (env.needIntro) await this.sendIntro(fromPeerId, false);
      this.sendAck(fromPeerId, env.id);
      return;
    }

    // ② 已知对端（signPub 已就绪）先验签
    if (!verifyEnvelope(env, contact.signPub)) {
      this.reportTampered(env);
      return;
    }

    switch (env.kind) {
      case 'ack': {
        this.resolveAck(env.msgId ?? '');
        return;
      }
      case 'read': {
        if (env.msgId) {
          await this.opts.storage.messages.updateStatus(env.msgId, 'read');
          useChatStore.getState().updateStatus(fromPeerId, env.msgId, 'read');
        }
        return;
      }
      case 'intro': {
        await this.upsertFromIntro(env, fromPeerId);
        if (env.needIntro) await this.sendIntro(fromPeerId, false);
        this.sendAck(fromPeerId, env.id);
        return;
      }
      case 'msg': {
        await this.receiveMessage(env, fromPeerId, contact);
        this.sendAck(fromPeerId, env.id);
        return;
      }
      default:
        // tombstone / signal 不在此处理（signal 已被传输层拦截）
        return;
    }
  }

  // ─────────────── Messenger：可靠送达 ───────────────

  /**
   * 可靠发送：pending 落库 → 发送 → 等 ACK → 超时重试（最多 3 次）。
   * 中继不可用时不丢消息，保留 pending 等 `flushPendingQueue()` 补发。
   */
  async sendReliable(to: string, envelope: ChatEnvelope): Promise<void> {
    const msgId = envelope.id;
    const item: PendingItem = { msgId, to, envelope, retries: 0, createdAt: Date.now() };
    await this.opts.storage.pending.put(item);
    await this.attemptSend(item);
  }

  /** 页面刷新 / 重连后补发队列里的消息。 */
  async flushPendingQueue(): Promise<number> {
    const items = await this.opts.storage.pending.all();
    if (items.length === 0) return 0;
    logger.info('恢复待发消息', items.length, '条');
    for (const item of items.sort((a, b) => a.createdAt - b.createdAt)) {
      void this.attemptSend(item).catch((err) => {
        logger.warn('队列补发失败', err instanceof Error ? err.message : err);
      });
    }
    return items.length;
  }

  private attemptSend(item: PendingItem): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.pendingAcks.has(item.msgId)) {
        resolve();
        return;
      }

      const timer = setTimeout(() => {
        void this.onAckTimeout(item).then(resolve).catch(reject);
      }, ACK_TIMEOUT_MS);

      const entry: PendingAck = { msgId: item.msgId, to: item.to, timer, resolve, reject };
      this.pendingAcks.set(item.msgId, entry);

      this.opts.transport
        .send(item.to, item.envelope)
        .then((result) => {
          // 入邮箱：状态转 ✓ 已发送，但仍等对方上线后的 ACK
          const status: MsgStatus = result.stored ? 'sent' : 'sending';
          void this.setStatus(item.to, item.msgId, status);
          logger.debug('已发送', item.msgId, result.kind, result.stored ? '(入邮箱)' : '');
        })
        .catch((err: unknown) => {
          clearTimeout(timer);
          this.pendingAcks.delete(item.msgId);
          const code = err instanceof MeshLinkError ? err.code : 'E_RELAY_UNAVAILABLE';
          if (code === 'E_RELAY_UNAVAILABLE') {
            // 保留 pending，等下一次 flush；消息状态回到 sending
            logger.warn('中继不可用，消息留在队列', item.msgId);
            void this.setStatus(item.to, item.msgId, 'sending');
            resolve();
            return;
          }
          void this.setStatus(item.to, item.msgId, 'failed');
          void this.opts.storage.pending.delete(item.msgId);
          reject(err);
        });
    });
  }

  private async onAckTimeout(item: PendingItem): Promise<void> {
    this.pendingAcks.delete(item.msgId);
    if (item.retries + 1 < MAX_RETRIES) {
      const next: PendingItem = { ...item, retries: item.retries + 1, createdAt: Date.now() };
      await this.opts.storage.pending.put(next);
      logger.info('ACK 超时，重试', next.retries, '/', MAX_RETRIES, item.msgId);
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      await this.attemptSend(next);
      return;
    }
    logger.warn('重试耗尽，标记失败', item.msgId);
    await this.setStatus(item.to, item.msgId, 'failed');
    await this.opts.storage.pending.delete(item.msgId);
  }

  private resolveAck(msgId: string): void {
    const pending = this.pendingAcks.get(msgId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingAcks.delete(msgId);
    void this.setStatus(pending.to, msgId, 'delivered');
    void this.opts.storage.pending.delete(msgId);
    pending.resolve();
    logger.debug('收到 ACK', msgId);
  }

  // ─────────────── 分页 ───────────────

  /** 加载一页消息（上拉加载，倒序取回后反正序）。 */
  async loadMessages(peerId: string, beforeTs?: number, limit = 30): Promise<Message[]> {
    const list = await this.opts.storage.messages.page(peerId, beforeTs, limit);
    useChatStore.getState().setMessages(peerId, list, beforeTs !== undefined);
    return list;
  }

  async countUnread(peerId: string): Promise<number> {
    return this.opts.storage.messages.countUnread(peerId);
  }

  // ─────────────── 内部 ───────────────

  private async receiveMessage(env: ChatEnvelope, peerId: string, contact: Contact): Promise<void> {
    // 幂等：同一 msgId 只入库一次（邮箱重投 / 重复推送）
    const dup = await this.opts.storage.messages.byMsgId(env.id);
    if (dup) {
      logger.debug('重复消息已忽略', env.id);
      return;
    }

    let body = '';
    if (env.ciphertext && env.iv) {
      try {
        const opened = await openEnvelope(env, contact.signPub, this.requireCipher(), contact.encPub);
        body = opened.plaintext ? decoder.decode(opened.plaintext) : '';
      } catch (err) {
        // 解密失败（非本端会话）也记验签失败，UI 提示「签名验证失败」
        logger.warn('解密失败', err instanceof Error ? err.message : err);
        useUiStore.getState().pushError('E_SIGNATURE_INVALID');
        return;
      }
    }

    const message: Message = {
      id: env.id,
      msgId: env.id,
      peerId,
      dir: 'in',
      kind: 'text',
      body,
      ciphertext: env.ciphertext,
      iv: env.iv,
      signature: env.signature,
      ts: env.timestamp,
      status: 'delivered',
    };
    await this.opts.storage.messages.put(message);
    useChatStore.getState().appendMessage(peerId, message);

    const isActive = useChatStore.getState().activePeerId === peerId;
    await this.touchContact(peerId, {
      lastText: body,
      lastTs: env.timestamp,
      unreadDelta: isActive ? 0 : 1,
    });

    if (isActive) {
      void this.markRead(peerId);
    }
  }

  private async upsertFromIntro(env: ChatEnvelope, peerId: string): Promise<void> {
    const existing = await this.opts.storage.contacts.get(peerId);
    const contact: Contact = {
      peerId,
      did: env.did ?? '',
      name: env.name || existing?.name || `好友_${peerId.slice(-4)}`,
      encPub: env.encPub ?? existing?.encPub ?? '',
      signPub: env.signPub ?? existing?.signPub ?? '',
      introState: env.encPub && env.signPub ? 'ready' : 'pending',
      avatarCid: existing?.avatarCid,
      lastText: existing?.lastText,
      lastTs: existing?.lastTs,
      unread: existing?.unread ?? 0,
      muted: existing?.muted ?? false,
      blocked: existing?.blocked ?? false,
      createdAt: existing?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
    };
    await this.opts.storage.contacts.put(contact);
    useChatStore.getState().upsertContact(contact);
    logger.info('intro 完成', peerId.slice(0, 12), contact.introState);
  }

  private sendAck(to: string, msgId: string): void {
    const keys = this.opts.identity.requireUnlocked();
    const envelope = buildEnvelope({
      kind: 'ack',
      from: this.opts.identity.did,
      to,
      signSeed: keys.signSeed,
      encPub: bytesToBase64(keys.encPub),
      sequence: this.nextSequence(to),
      msgId,
    });
    this.opts.transport.sendRaw(to, envelope);
  }

  private reportTampered(env: ChatEnvelope): void {
    logger.warn('信封验签失败，已丢弃', env.id, env.from?.slice(0, 16));
    useUiStore.getState().pushError('E_SIGNATURE_INVALID');
  }

  private async setStatus(peerId: string, msgId: string, status: MsgStatus): Promise<void> {
    await this.opts.storage.messages.updateStatus(msgId, status).catch(() => undefined);
    useChatStore.getState().updateStatus(peerId, msgId, status);
  }

  private async touchContact(
    peerId: string,
    patch: { lastText?: string; lastTs?: number; unreadDelta?: number },
  ): Promise<void> {
    const contact = await this.opts.storage.contacts.get(peerId);
    if (!contact) return;
    const next: Contact = {
      ...contact,
      lastText: patch.lastText ?? contact.lastText,
      lastTs: patch.lastTs ?? contact.lastTs,
      unread: Math.max(0, contact.unread + (patch.unreadDelta ?? 0)),
      updatedAt: Date.now(),
    };
    await this.opts.storage.contacts.put(next);
    useChatStore.getState().upsertContact(next);
  }

  private nextSequence(peerId: string): number {
    const next = (this.sequences.get(peerId) ?? 0) + 1;
    this.sequences.set(peerId, next);
    return next;
  }

  private requireCipher(): ISessionCipher {
    if (this.cipher) return this.cipher;
    const seed = this.opts.identity.sessionSeed();
    const factory = this.cipherFactory;
    this.cipher = factory
      ? factory({ encSeed: seed.encSeed, encPub: seed.encPub })
      : new EcdhAesGcmCipher({ encSeed: seed.encSeed, encPub: seed.encPub });
    return this.cipher;
  }

  /** 锁定 / 注销时调用：销毁内存会话密钥与序号表。 */
  destroy(): void {
    this.stop();
    this.cipher?.reset();
    this.cipher = null;
    this.sequences.clear();
  }

  /** 测试辅助：注入自定义 cipher。 */
  setCipher(cipher: ISessionCipher): void {
    this.cipher = cipher;
  }

  /** 测试辅助：当前正在等 ACK 的消息数。 */
  pendingCount(): number {
    return this.pendingAcks.size;
  }

  /**
   * 与某对端的 6 位安全码（双方一致，供线下比对防中间人）。
   * 未完成 intro 时返回空串。
   */
  async safetyCode(peerId: string): Promise<string> {
    const contact = await this.opts.storage.contacts.get(peerId);
    if (!contact?.encPub) return '';
    return this.requireCipher().safetyCode({ encPub: contact.encPub, signPub: contact.signPub });
  }
}

/** base64 → UTF-8 字符串（收端解密后用）。 */
export function decodeBody(bytes: Uint8Array): string {
  return decoder.decode(bytes);
}

/** UTF-8 → base64 字节。 */
export function encodeBody(text: string): Uint8Array {
  return encoder.encode(text);
}

/** 供测试：从 base64 还原字节。 */
export function bytesFromB64(b64: string): Uint8Array {
  return base64ToBytes(b64);
}

export default ChatService;
